/**
 * 공통 다이얼로그 — 브라우저 기본 alert/confirm 대신 사용
 *
 *   const { confirm, alert, showLoading, hideLoading } = useDialog();
 *   if (await confirm({ title, message, confirmText, danger })) { ... }
 *   await alert('메시지');  또는  alert({ title, message });
 *   showLoading({ title, message });  ...  hideLoading();
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import styled from 'styled-components';

const DialogContext = createContext(null);

export const useDialog = () => useContext(DialogContext);

export const DialogProvider = ({ children }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(null);

  const showLoading = useCallback((opts) => {
    setLoading(typeof opts === 'string' ? { message: opts } : (opts || {}));
  }, []);

  const hideLoading = useCallback(() => setLoading(null), []);

  const confirm = useCallback((opts) => new Promise((resolve) => {
    setD({ mode: 'confirm', ...(typeof opts === 'string' ? { message: opts } : opts), resolve });
  }), []);

  const alert = useCallback((opts) => new Promise((resolve) => {
    setD({ mode: 'alert', ...(typeof opts === 'string' ? { message: opts } : opts), resolve });
  }), []);

  const finish = (result) => {
    if (d) d.resolve(result);
    setD(null);
  };

  return (
    <DialogContext.Provider value={{ confirm, alert, showLoading, hideLoading }}>
      {children}
      {loading && (
        <Overlay $dim>
          <LoadingBox>
            <LoaderWrap>
              <Halo />
              <Ring />
              <Dot />
            </LoaderWrap>
            <Title>{loading.title || '잠시만요'}</Title>
            {loading.message && <Message>{loading.message}</Message>}
          </LoadingBox>
        </Overlay>
      )}
      {d && (
        <Overlay onClick={() => finish(false)}>
          <Box onClick={(e) => e.stopPropagation()}>
            {d.title && <Title>{d.title}</Title>}
            {d.message && <Message>{d.message}</Message>}
            <Actions>
              {d.mode === 'confirm' && (
                <Cancel type="button" onClick={() => finish(false)}>
                  {d.cancelText || '취소'}
                </Cancel>
              )}
              <Confirm type="button" $danger={!!d.danger} onClick={() => finish(true)}>
                {d.confirmText || '확인'}
              </Confirm>
            </Actions>
          </Box>
        </Overlay>
      )}
    </DialogContext.Provider>
  );
};

export default DialogProvider;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9000;
  background: ${({ $dim }) => ($dim ? 'rgba(17, 24, 39, 0.58)' : 'rgba(0, 0, 0, 0.45)')};
  backdrop-filter: ${({ $dim }) => ($dim ? 'blur(3px)' : 'none')};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 32px;
  animation: dlgFade 0.2s ease;
  @keyframes dlgFade { from { opacity: 0; } to { opacity: 1; } }
`;

const Box = styled.div`
  width: 100%;
  max-width: 320px;
  background: #fff;
  border-radius: 18px;
  padding: 26px 22px 18px;
`;

const LoadingBox = styled.div`
  width: 100%;
  max-width: 290px;
  background: #fff;
  border-radius: 22px;
  padding: 34px 24px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 18px 50px rgba(17, 24, 39, 0.28);
  animation: dlgPop 0.32s cubic-bezier(0.34, 1.3, 0.64, 1);
  @keyframes dlgPop {
    from { opacity: 0; transform: translateY(14px) scale(0.94); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
`;

const LoaderWrap = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  margin-bottom: 18px;
`;

/* 펄스로 퍼지는 연한 헤일로 */
const Halo = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  opacity: 0.16;
  animation: halo 1.6s ease-in-out infinite;
  @keyframes halo {
    0%, 100% { transform: scale(0.7); opacity: 0.06; }
    50% { transform: scale(1); opacity: 0.2; }
  }
`;

/* 회전하는 그라데이션 링 */
const Ring = styled.div`
  position: absolute;
  inset: 9px;
  border-radius: 50%;
  border: 4px solid #EAEEF6;
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-right-color: ${({ theme }) => theme.colors.primary};
  animation: ringSpin 0.85s linear infinite;
  @keyframes ringSpin { to { transform: rotate(360deg); } }
`;

/* 가운데 살짝 뛰는 점 */
const Dot = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 11px;
  height: 11px;
  margin: -5.5px 0 0 -5.5px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  animation: dotPulse 1.6s ease-in-out infinite;
  @keyframes dotPulse {
    0%, 100% { transform: scale(0.6); opacity: 0.5; }
    50% { transform: scale(1); opacity: 1; }
  }
`;

const Title = styled.p`
  font-size: 17px;
  font-weight: 800;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Message = styled.p`
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
  white-space: pre-line;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 8px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 22px;
`;

const Cancel = styled.button`
  flex: 1;
  padding: 13px;
  border-radius: 12px;
  background: #F4F5F7;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Confirm = styled.button`
  flex: 1;
  padding: 13px;
  border-radius: 12px;
  background: ${({ $danger, theme }) => ($danger ? theme.colors.accent.red : theme.colors.primary)};
  color: #fff;
  font-size: 15px;
  font-weight: 700;
`;
